import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ReorderPanel from "@/components/admin/ReorderPanel";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Download, Upload, FolderOpen, Layers, Settings2, ChevronRight, X, Palette, Trophy, Eye, EyeOff, GripVertical, Star, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import SportBrandingTab from "@/pages/SportBrandingTab";
import RichTextEditor from "@/components/RichTextEditor";
import Pagination from "@/components/Pagination";
import { sanitize } from "@/lib/sanitize";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number (integer)" },
  { value: "decimal", label: "Decimal" },
  { value: "date", label: "Date" },
  { value: "dropdown", label: "Dropdown" },
  { value: "boolean", label: "Boolean (yes/no)" },
  { value: "textarea", label: "Multi-line Text" },
];

const formatCv = (v, f) => {
  if (v === undefined || v === null || v === "") return <span className="text-slate-400">—</span>;
  if (f.type === "boolean") return v === true || v === "true" ? "Yes" : "No";
  return String(v);
};

export default function SportAdminPage() {
  const { user, can } = useAuth();
  const [sports, setSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [sportSearch, setSportSearch] = useState("");
  const [tab, setTab] = useState("records"); // records | categories | fields | info
  const [showNewSport, setShowNewSport] = useState(false);
  const [editSport, setEditSport] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { type, id, name, action }

  const loadSports = async (preferId) => {
    const r = await api.get("/sports");
    setSports(r.data);
    const targetId = preferId || selectedSport?.id;
    if (targetId) {
      const upd = r.data.find(s => s.id === targetId);
      if (upd) { setSelectedSport(upd); return; }
    }
    if (!selectedSport && r.data.length) setSelectedSport(r.data[0]);
  };
  useEffect(() => { if (user) loadSports(); }, [user]);

  if (user === undefined) return <div className="p-8">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!can("manage_sports") && !can("reorder_records")) return <Navigate to="/dashboard" replace />;

  const canManage = can("manage_sports");
  const canReorder = can("reorder_records");
  const isSuperAdmin = user.role === "super_admin";

  const filteredSports = sports.filter(s => s.name.toLowerCase().includes(sportSearch.toLowerCase()));

  return (
    <main className="max-w-[1400px] mx-auto px-4 py-6" data-testid="sport-admin-page">
      <div className="mb-4 border-b-2 border-[#0A1128] pb-3">
        <p className="font-overline text-xs text-[#E11D48]">Administration</p>
        <h1 className="font-heading text-3xl tracking-tight">SPORT MANAGEMENT</h1>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* LEFT NAV */}
        <aside className="col-span-12 md:col-span-3 lg:col-span-3 bg-white border border-slate-300" data-testid="sports-nav">
          <div className="p-3 border-b bg-slate-50">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
              <Input placeholder="Search sports..." value={sportSearch} onChange={e => setSportSearch(e.target.value)}
                className="pl-8 h-9 rounded-sm text-sm" data-testid="sports-search"/>
            </div>
            {canManage && (
              <Button size="sm" className="w-full mt-2 rounded-sm bg-[#0A1128] hover:bg-slate-800 h-8"
                onClick={() => setShowNewSport(true)} data-testid="add-sport-btn">
                <Plus className="w-3.5 h-3.5 mr-1"/>Add Sport
              </Button>
            )}
          </div>
          <ul className="max-h-[70vh] overflow-y-auto">
            {filteredSports.length === 0 && (
              <li className="p-4 text-xs text-slate-500 text-center">No sports match your search.</li>
            )}
            {filteredSports.map(s => {
              const hidden = s.is_public === false;
              return (
              <li key={s.id}>
                <button onClick={() => setSelectedSport(s)}
                  className={`w-full text-left px-3 py-2.5 border-b border-slate-100 flex items-center justify-between group ${selectedSport?.id === s.id ? "bg-[#0A1128] text-white" : "hover:bg-slate-50"} ${hidden ? "opacity-70" : ""}`}
                  data-testid={`sport-nav-${s.slug}`}>
                  <div className="min-w-0">
                    <div className="font-heading text-sm tracking-wide flex items-center gap-1.5">
                      {s.name}
                      {hidden && <EyeOff className="w-3 h-3 opacity-60" data-testid={`sport-hidden-icon-${s.slug}`}/>}
                    </div>
                    <div className={`text-[10px] ${selectedSport?.id === s.id ? "text-slate-300" : "text-slate-500"}`}>
                      {s.record_count} records · {s.categories?.length || 0} categories{hidden ? " · hidden" : ""}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${selectedSport?.id === s.id ? "opacity-100" : "opacity-0 group-hover:opacity-50"}`}/>
                </button>
              </li>
              );
            })}
          </ul>
        </aside>

        {/* MAIN */}
        <section className="col-span-12 md:col-span-9 lg:col-span-9">
          {!selectedSport ? (
            <div className="border-2 border-dashed border-slate-300 p-16 text-center text-slate-500 bg-white">
              <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-50"/>
              Select a sport from the left, or add a new one.
            </div>
          ) : (
            <div className="bg-white border border-slate-300">
              <div className="p-4 border-b flex items-start justify-between gap-4">
                <div>
                  <p className="font-overline text-[10px] text-slate-500">Sport</p>
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading text-3xl tracking-tight">{selectedSport.name}</h2>
                    {selectedSport.is_public === false && (
                      <Badge className="rounded-sm bg-red-50 text-red-700 border border-red-300 font-heading tracking-wide" data-testid="sport-hidden-badge">
                        <EyeOff className="w-3 h-3 mr-1"/>HIDDEN FROM PUBLIC
                      </Badge>
                    )}
                  </div>
                  {selectedSport.description && <p className="text-sm text-slate-600 mt-1 max-w-xl">{selectedSport.description}</p>}
                </div>
                <div className="flex gap-2 items-center">
                  {isSuperAdmin && (
                    <Button size="sm" variant="outline" className="rounded-sm" onClick={async () => {
                      const next = selectedSport.is_public === false;
                      await api.patch(`/sports/${selectedSport.id}`, { is_public: next });
                      toast.success(next ? `${selectedSport.name} is now visible to the public` : `${selectedSport.name} is now hidden from the public`);
                      loadSports(selectedSport.id);
                    }} data-testid="toggle-public-btn">
                      {selectedSport.is_public === false ? <><Eye className="w-3.5 h-3.5 mr-1"/>Publish</> : <><EyeOff className="w-3.5 h-3.5 mr-1"/>Hide</>}
                    </Button>
                  )}
                  {canManage && (
                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditSport(selectedSport)} data-testid="edit-sport-btn">
                      <Pencil className="w-3.5 h-3.5 mr-1"/>Edit
                    </Button>
                  )}
                  {isSuperAdmin && (
                    <Button size="sm" variant="outline" className="rounded-sm text-red-600 border-red-300"
                      onClick={() => setConfirmDelete({ type: "sport", id: selectedSport.id, name: selectedSport.name, action: async () => {
                        await api.delete(`/sports/${selectedSport.id}`); toast.success("Sport deleted"); setSelectedSport(null); loadSports();
                      }})}
                      data-testid="delete-sport-btn">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex border-b bg-slate-50 overflow-x-auto">
                {[
                  ["records", "Records", Layers],
                  ...(canManage ? [
                    ["types", "Record Types", Trophy],
                    ["categories", "Categories", FolderOpen],
                    ["fields", "Custom Fields", Settings2],
                    ["branding", "Branding", Palette],
                  ] : []),
                ].map(([id, label, Icon]) => (
                  <button key={id} onClick={() => setTab(id)}
                    className={`px-4 py-2.5 text-sm font-heading tracking-wide border-b-2 flex items-center gap-2 whitespace-nowrap ${tab === id ? "border-[#0A1128] text-[#0A1128] bg-white" : "border-transparent text-slate-500 hover:text-[#0A1128]"}`}
                    data-testid={`admin-tab-${id}`}>
                    <Icon className="w-4 h-4"/>{label}
                  </button>
                ))}
              </div>

              <div className="p-4">
                {tab === "records" && <RecordsView sport={selectedSport} canManage={canManage} canReorder={canReorder} setConfirmDelete={setConfirmDelete}/>}
                {tab === "types" && <RecordTypesView sport={selectedSport} canManage={canManage} isSuperAdmin={isSuperAdmin} setConfirmDelete={setConfirmDelete}/>}
                {tab === "categories" && <CategoriesView sport={selectedSport} canManage={canManage} isSuperAdmin={isSuperAdmin} setConfirmDelete={setConfirmDelete} onChange={loadSports}/>}
                {tab === "fields" && <CustomFieldsView sport={selectedSport} canManage={canManage} isSuperAdmin={isSuperAdmin} setConfirmDelete={setConfirmDelete}/>}
                {tab === "branding" && <SportBrandingTab sport={selectedSport} canManage={canManage} onSaved={() => loadSports(selectedSport.id)}/>}
              </div>
            </div>
          )}
        </section>
      </div>

      {showNewSport && <SportDialog onClose={() => setShowNewSport(false)} onSaved={s => { setSelectedSport(s); loadSports(s.id); }}/>}
      {editSport && <SportDialog sport={editSport} onClose={() => setEditSport(null)} onSaved={() => loadSports()}/>}
      {confirmDelete && (
        <AlertDialog open={true} onOpenChange={o => !o && setConfirmDelete(null)}>
          <AlertDialogContent className="rounded-none border-2 border-[#0A1128]">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">DELETE {confirmDelete.type.toUpperCase()}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{confirmDelete.name}</strong>{confirmDelete.type === "sport" ? " and all its records" : confirmDelete.type === "category" ? " and all records in it" : ""}. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
                onClick={async () => { await confirmDelete.action(); setConfirmDelete(null); }}
                data-testid="confirm-delete-btn">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </main>
  );
}

// ---------- Sport Dialog (create/edit) ----------
function SportDialog({ sport, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: sport?.name || "",
    image_url: sport?.image_url || "",
    description: sport?.description || "",
    is_public: sport?.is_public !== false, // default true
  });
  const save = async () => {
    try {
      let result;
      if (sport) {
        const { data } = await api.patch(`/sports/${sport.id}`, form);
        result = data; toast.success("Sport updated");
      } else {
        const { data } = await api.post("/sports", form);
        result = data; toast.success("Sport created");
      }
      onSaved(result); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="rounded-none border-2 border-[#0A1128] max-w-lg" data-testid="sport-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">{sport ? "EDIT SPORT" : "NEW SPORT"}</DialogTitle>
          <DialogDescription>Sports are top-level containers for record categories.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Sport name *</Label>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-sm" data-testid="sport-name-input"/>
          </div>
          <div>
            <Label>Image URL</Label>
            <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} className="rounded-sm" data-testid="sport-image-input"/>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-sm min-h-24" data-testid="sport-description-input"/>
          </div>
          <div className="border border-slate-200 bg-slate-50 p-3 flex items-start gap-3">
            <Checkbox id="is-public" checked={form.is_public} onCheckedChange={v => setForm({...form, is_public: !!v})} data-testid="sport-is-public-input" className="mt-0.5"/>
            <div>
              <Label htmlFor="is-public" className="cursor-pointer font-heading tracking-wide">Visible to Public</Label>
              <p className="text-xs text-slate-600 mt-0.5">
                {form.is_public
                  ? "This sport appears on the public site, home page, master search, and navigation."
                  : "This sport is hidden from public. Only Super Admins can see it in the dashboard."}
              </p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-[#0A1128] hover:bg-slate-800" data-testid="save-sport-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Categories View ----------
function CategoriesView({ sport, canManage, isSuperAdmin, setConfirmDelete, onChange }) {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const load = async () => {
    const r = await api.get(`/categories?sport_id=${sport.id}`);
    setCats(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sport.id]);

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (idx, e) => { e.preventDefault(); setOverIdx(idx); };
  const onDrop = async (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...cats];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setCats(next);
    setDragIdx(null); setOverIdx(null);
    try {
      await api.post("/categories/reorder", { ids: next.map(c => c.id) });
      toast.success("Category order saved");
      onChange && onChange();
    } catch (e) { toast.error("Failed to save order"); load(); }
  };

  return (
    <div>
      {canManage && (
        <div className="flex justify-between items-center mb-3">
          <p className="text-xs text-slate-500">
            <GripVertical className="w-3 h-3 inline mr-1 -mt-0.5"/>Drag rows to reorder how categories appear in the navigation tabs.
          </p>
          <Button size="sm" onClick={() => setShowNew(true)} className="rounded-sm bg-[#0A1128] hover:bg-slate-800" data-testid="add-category-btn">
            <Plus className="w-3.5 h-3.5 mr-1"/>Add Category
          </Button>
        </div>
      )}
      {cats.length === 0 ? (
        <div className="border-2 border-dashed border-slate-300 p-10 text-center text-slate-500">
          No categories yet. Categories group records (e.g., "Rushing Records", "Career Points").
        </div>
      ) : (
        <div className="border border-slate-200">
          <Table>
            <TableHeader className="bg-slate-100">
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="font-heading">Name</TableHead>
                <TableHead className="font-heading">Description</TableHead>
                <TableHead className="font-heading text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cats.map((c, i) => (
                <TableRow key={c.id}
                  draggable={canManage}
                  onDragStart={() => onDragStart(i)}
                  onDragOver={(e) => onDragOver(i, e)}
                  onDrop={() => onDrop(i)}
                  onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                  className={`${overIdx === i && dragIdx !== null && dragIdx !== i ? "border-t-2 border-t-[#FACC15]" : ""} ${dragIdx === i ? "opacity-50" : ""}`}
                  data-testid={`category-row-${c.id}`}>
                  <TableCell className="cursor-grab text-slate-400" title="Drag to reorder">
                    <GripVertical className="w-4 h-4"/>
                  </TableCell>
                  <TableCell className="font-medium">
                    {c.name}
                    {c.is_default && (
                      <Badge className="ml-2 rounded-sm bg-yellow-100 text-yellow-800 border border-yellow-300 text-[10px] font-heading tracking-wide" data-testid={`default-badge-${c.id}`}>
                        <Star className="w-2.5 h-2.5 mr-1 fill-current"/>DEFAULT
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-md truncate">{c.description ? <span dangerouslySetInnerHTML={{__html: c.description.replace(/<[^>]+>/g, ' ').slice(0,120)}}/> : <span className="text-slate-400 italic">—</span>}</TableCell>
                  <TableCell className="text-right">
                    {canManage && (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" title={c.is_default ? "Already the default" : "Set as default"}
                          className={`rounded-sm h-8 px-2 ${c.is_default ? "bg-yellow-50 text-yellow-800 border-yellow-300" : ""}`}
                          disabled={c.is_default}
                          onClick={async () => {
                            await api.patch(`/categories/${c.id}`, { is_default: true });
                            toast.success(`"${c.name}" is now the default category`);
                            load();
                          }}
                          data-testid={`set-default-${c.id}`}>
                          <Star className={`w-3 h-3 mr-1 ${c.is_default ? "fill-current" : ""}`}/>{c.is_default ? "Default" : "Set as Default"}
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0" onClick={() => setEditing(c)} data-testid={`edit-cat-${c.id}`}><Pencil className="w-3 h-3"/></Button>
                        {isSuperAdmin && (
                          <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0 text-red-600"
                            onClick={() => setConfirmDelete({ type: "category", id: c.id, name: c.name, action: async () => {
                              await api.delete(`/categories/${c.id}`); toast.success("Category deleted"); load(); onChange && onChange();
                            }})}
                            data-testid={`del-cat-${c.id}`}><Trash2 className="w-3 h-3"/></Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {(showNew || editing) && <CategoryDialog sport={sport} cat={editing} onClose={() => { setShowNew(false); setEditing(null); }} onSaved={() => { load(); onChange && onChange(); }}/>}
    </div>
  );
}

function CategoryDialog({ sport, cat, onClose, onSaved }) {
  const [form, setForm] = useState({ name: cat?.name || "", description: cat?.description || "" });
  const save = async () => {
    try {
      if (cat) await api.patch(`/categories/${cat.id}`, form);
      else await api.post("/categories", { sport_id: sport.id, ...form });
      toast.success(cat ? "Category updated" : "Category created");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="rounded-none border-2 border-[#0A1128] max-w-md" data-testid="category-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{cat ? "EDIT CATEGORY" : "NEW CATEGORY"}</DialogTitle>
          <DialogDescription>Categories group related records within a sport.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-sm" data-testid="category-name-input"/>
          </div>
          <div>
            <Label>Description / Record Explanation</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-sm min-h-24" placeholder="Explain what this record measures..." data-testid="category-description-input"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-[#0A1128] hover:bg-slate-800" data-testid="save-category-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Custom Fields View ----------
function CustomFieldsView({ sport, canManage, isSuperAdmin, setConfirmDelete }) {
  const [fields, setFields] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const r = await api.get(`/custom-fields?sport_id=${sport.id}`);
    setFields(r.data);
  };
  useEffect(() => { load(); }, [sport.id]);

  const move = async (idx, dir) => {
    const arr = [...fields];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setFields(arr);
    await api.post("/custom-fields/reorder", { ids: arr.map(f => f.id) });
  };

  return (
    <div>
      <p className="text-sm text-slate-600 mb-3">
        Custom fields extend record entries for this sport.
      </p>
      {canManage && (
        <div className="flex justify-end mb-3">
          <Button size="sm" onClick={() => setShowNew(true)} className="rounded-sm bg-[#0A1128] hover:bg-slate-800" data-testid="add-field-btn">
            <Plus className="w-3.5 h-3.5 mr-1"/>Add Custom Field
          </Button>
        </div>
      )}
      {fields.length === 0 ? (
        <div className="border-2 border-dashed border-slate-300 p-10 text-center text-slate-500">
          No custom fields yet. Add fields like "Position", "Opponent", "Was Championship" to capture sport-specific data.
        </div>
      ) : (
        <div className="border border-slate-200">
          <Table>
            <TableHeader className="bg-slate-100">
              <TableRow>
                <TableHead className="w-20 font-heading">Order</TableHead>
                <TableHead className="font-heading">Label</TableHead>
                <TableHead className="font-heading">Key</TableHead>
                <TableHead className="font-heading">Type</TableHead>
                <TableHead className="font-heading">Required</TableHead>
                <TableHead className="font-heading text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((f, i) => (
                <TableRow key={f.id} data-testid={`field-row-${f.id}`}>
                  <TableCell>
                    <div className="flex gap-0.5">
                      <Button size="sm" variant="outline" className="h-6 w-6 p-0 rounded-sm" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`field-up-${f.id}`}><ArrowUp className="w-3 h-3"/></Button>
                      <Button size="sm" variant="outline" className="h-6 w-6 p-0 rounded-sm" disabled={i === fields.length - 1} onClick={() => move(i, 1)} data-testid={`field-down-${f.id}`}><ArrowDown className="w-3 h-3"/></Button>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{f.label}</TableCell>
                  <TableCell><code className="text-xs bg-slate-100 px-1.5 py-0.5">{f.key}</code></TableCell>
                  <TableCell><Badge className="rounded-sm bg-slate-100 text-slate-700 border">{FIELD_TYPES.find(t => t.value === f.type)?.label || f.type}</Badge></TableCell>
                  <TableCell>{f.required ? <Badge className="rounded-sm bg-red-50 text-red-700 border border-red-200">Required</Badge> : <span className="text-slate-400 text-xs">Optional</span>}</TableCell>
                  <TableCell className="text-right">
                    {canManage && (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0" onClick={() => setEditing(f)} data-testid={`edit-field-${f.id}`}><Pencil className="w-3 h-3"/></Button>
                        {isSuperAdmin && (
                          <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0 text-red-600"
                            onClick={() => setConfirmDelete({ type: "custom field", id: f.id, name: f.label, action: async () => {
                              await api.delete(`/custom-fields/${f.id}`); toast.success("Field deleted"); load();
                            }})}
                            data-testid={`del-field-${f.id}`}><Trash2 className="w-3 h-3"/></Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {(showNew || editing) && <CustomFieldDialog sport={sport} field={editing} onClose={() => { setShowNew(false); setEditing(null); }} onSaved={load}/>}
    </div>
  );
}

function CustomFieldDialog({ sport, field, onClose, onSaved }) {
  const [form, setForm] = useState({
    key: field?.key || "",
    label: field?.label || "",
    type: field?.type || "text",
    required: field?.required || false,
    default_value: field?.default_value || "",
    placeholder: field?.placeholder || "",
    help_text: field?.help_text || "",
    options: (field?.options || []).join(", "),
  });
  const save = async () => {
    const payload = { ...form, options: form.type === "dropdown" ? form.options.split(",").map(s => s.trim()).filter(Boolean) : null };
    try {
      if (field) await api.patch(`/custom-fields/${field.id}`, payload);
      else await api.post("/custom-fields", { sport_id: sport.id, ...payload });
      toast.success(field ? "Field updated" : "Field created");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="rounded-none border-2 border-[#0A1128] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="field-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{field ? "EDIT CUSTOM FIELD" : "NEW CUSTOM FIELD"}</DialogTitle>
          <DialogDescription>Custom fields appear in record entry forms for this sport.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Label *</Label>
              <Input value={form.label} onChange={e => setForm({...form, label: e.target.value, key: field ? form.key : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")})} className="rounded-sm" data-testid="field-label-input"/>
            </div>
            <div>
              <Label>Key * (snake_case)</Label>
              <Input value={form.key} onChange={e => setForm({...form, key: e.target.value})} className="rounded-sm font-mono text-sm" disabled={!!field} data-testid="field-key-input"/>
            </div>
          </div>
          <div>
            <Label>Type *</Label>
            <Select value={form.type} onValueChange={v => setForm({...form, type: v})} disabled={!!field}>
              <SelectTrigger className="rounded-sm" data-testid="field-type-input"><SelectValue/></SelectTrigger>
              <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.type === "dropdown" && (
            <div>
              <Label>Options (comma-separated)</Label>
              <Input value={form.options} onChange={e => setForm({...form, options: e.target.value})} className="rounded-sm" placeholder="Option 1, Option 2, Option 3" data-testid="field-options-input"/>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Checkbox id="required" checked={form.required} onCheckedChange={v => setForm({...form, required: !!v})} data-testid="field-required-input"/>
            <Label htmlFor="required" className="cursor-pointer">Required</Label>
          </div>
          <div>
            <Label>Default value</Label>
            <Input value={form.default_value} onChange={e => setForm({...form, default_value: e.target.value})} className="rounded-sm" data-testid="field-default-input"/>
          </div>
          <div>
            <Label>Placeholder</Label>
            <Input value={form.placeholder} onChange={e => setForm({...form, placeholder: e.target.value})} className="rounded-sm" data-testid="field-placeholder-input"/>
          </div>
          <div>
            <Label>Help text</Label>
            <Input value={form.help_text} onChange={e => setForm({...form, help_text: e.target.value})} className="rounded-sm" placeholder="Shown below the input" data-testid="field-help-input"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-[#0A1128] hover:bg-slate-800" data-testid="save-field-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Record Types View ----------
function RecordTypesView({ sport, canManage, isSuperAdmin, setConfirmDelete }) {
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const load = async () => {
    const [rt, r] = await Promise.all([
      api.get(`/record-types?sport_id=${sport.id}`),
      api.get(`/records?sport_id=${sport.id}`),
    ]);
    const counts = {};
    r.data.forEach(rec => {
      const key = rec.record_type || "";
      counts[key] = (counts[key] || 0) + 1;
    });
    setTypes(rt.data.map(t => ({ ...t, record_count: counts[t.name] || 0 })));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sport.id]);

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (i, e) => { e.preventDefault(); setOverIdx(i); };
  const onDrop = async (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...types];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setTypes(next); setDragIdx(null); setOverIdx(null);
    try {
      await api.post("/record-types/reorder", { ids: next.map(t => t.id) });
      toast.success("Record type order saved");
    } catch (e) { toast.error("Failed to save order"); load(); }
  };

  return (
    <div>
      <p className="text-sm text-slate-600 mb-1">
        Record Types are top-level tabs on the public sport page (e.g. "Team Champions", "Finals Records", "All-Time Tournament Results").
      </p>
      {canManage && (
        <div className="flex justify-between items-center mb-3">
          <p className="text-xs text-slate-500"><GripVertical className="w-3 h-3 inline mr-1 -mt-0.5"/>Drag rows to reorder how tabs appear.</p>
          <Button size="sm" onClick={() => setShowNew(true)} className="rounded-sm bg-[#0A1128] hover:bg-slate-800" data-testid="add-record-type-btn">
            <Plus className="w-3.5 h-3.5 mr-1"/>Add Record Type
          </Button>
        </div>
      )}
      {types.length === 0 ? (
        <div className="border-2 border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="no-record-types">
          No record types yet. Add a top-level grouping for records.
        </div>
      ) : (
        <div className="border border-slate-200">
          <Table>
            <TableHeader className="bg-slate-100">
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="font-heading">Name</TableHead>
                <TableHead className="font-heading">Description</TableHead>
                <TableHead className="font-heading w-24">Records</TableHead>
                <TableHead className="font-heading w-40">Grouping</TableHead>
                <TableHead className="font-heading text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {types.map((t, i) => (
                <TableRow key={t.id}
                  draggable={canManage}
                  onDragStart={() => onDragStart(i)}
                  onDragOver={(e) => onDragOver(i, e)}
                  onDrop={() => onDrop(i)}
                  onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                  className={`${overIdx === i && dragIdx !== null && dragIdx !== i ? "border-t-2 border-t-[#FACC15]" : ""} ${dragIdx === i ? "opacity-50" : ""}`}
                  data-testid={`record-type-row-${t.id}`}>
                  <TableCell className="cursor-grab text-slate-400" title="Drag to reorder"><GripVertical className="w-4 h-4"/></TableCell>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-md truncate">{t.description ? <span dangerouslySetInnerHTML={{__html: t.description.replace(/<[^>]+>/g,' ').slice(0,120)}}/> : <span className="text-slate-400 italic">—</span>}</TableCell>
                  <TableCell><Badge className="rounded-sm bg-slate-100 border">{t.record_count}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {t.group_by_event_tabs
                      ? <Badge className="rounded-sm bg-[#FACC15]/20 text-[#0A1128] border border-[#FACC15]">Tabs · {t.event_column || "?"}</Badge>
                      : <span className="text-slate-400 italic">Single table</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage && (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0" onClick={() => setEditing(t)} data-testid={`edit-record-type-${t.id}`}><Pencil className="w-3 h-3"/></Button>
                        {isSuperAdmin && (
                          <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0 text-red-600"
                            onClick={() => setConfirmDelete({ type: "record type", id: t.id, name: t.name, action: async () => {
                              await api.delete(`/record-types/${t.id}`); toast.success("Record type deleted; its records were moved to General");
                              load();
                            }})}
                            data-testid={`del-record-type-${t.id}`}><Trash2 className="w-3 h-3"/></Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {(showNew || editing) && <RecordTypeDialog sport={sport} rt={editing} onClose={() => { setShowNew(false); setEditing(null); }} onSaved={load}/>}
    </div>
  );
}

function RecordTypeDialog({ sport, rt, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: rt?.name || "",
    description: rt?.description || "",
    default_sort_field: rt?.default_sort_field || "year",
    default_sort_dir: rt?.default_sort_dir || "desc",
    group_by_event_tabs: rt?.group_by_event_tabs || false,
    event_column: rt?.event_column || "",
  });
  const columns = (rt?.columns || []).filter(Boolean);
  // Auto-pick a sensible default event column the first time the toggle is switched on
  useEffect(() => {
    if (form.group_by_event_tabs && !form.event_column && columns.length) {
      const preferred = columns.find(c => /event/i.test(c)) || columns[0];
      setForm(f => ({ ...f, event_column: preferred }));
    }
  }, [form.group_by_event_tabs]); // eslint-disable-line
  const save = async () => {
    try {
      // Only send event_column when grouping is enabled to keep the payload clean.
      const payload = { ...form };
      if (!form.group_by_event_tabs) payload.event_column = null;
      if (rt) await api.patch(`/record-types/${rt.id}`, payload);
      else await api.post("/record-types", { sport_id: sport.id, ...payload });
      toast.success(rt ? "Record type updated" : "Record type created");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="rounded-none border-2 border-[#0A1128] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="record-type-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{rt ? "EDIT RECORD TYPE" : "NEW RECORD TYPE"}</DialogTitle>
          <DialogDescription>Record types become tabs on the public sport page. The description shows at the top of that tab.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-sm" placeholder="Team Champions" data-testid="record-type-name-input"/>
          </div>
          <div>
            <Label>Description</Label>
            <RichTextEditor
              value={form.description}
              onChange={v => setForm({...form, description: v})}
              testid="record-type-description-input"
              placeholder="Explain what this record type covers. Use bold, color, or links..."
            />
            <p className="text-[10px] text-slate-500 mt-1">Rich text: bold, color, and hyperlinks are preserved on the public page.</p>
          </div>

          {rt && (
            <div className="border-t pt-3 space-y-2">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="group-by-event-toggle-label">
                <Checkbox
                  checked={form.group_by_event_tabs}
                  onCheckedChange={v => setForm({...form, group_by_event_tabs: !!v})}
                  className="mt-0.5"
                  data-testid="group-by-event-toggle"
                />
                <div>
                  <div className="font-medium text-sm">Group by Event Tabs</div>
                  <div className="text-xs text-slate-500">
                    When ON, records are split into sub-tabs on the public page based on their event column.
                    When OFF, all records display in a single continuous table.
                  </div>
                </div>
              </label>
              {form.group_by_event_tabs && (
                columns.length === 0 ? (
                  <div className="text-xs text-red-600 pl-6">
                    Import a CSV first to define columns for this record type before enabling grouping.
                  </div>
                ) : (
                  <div className="pl-6">
                    <Label className="text-xs">Event column</Label>
                    <Select value={form.event_column || "__none__"} onValueChange={v => setForm({...form, event_column: v === "__none__" ? "" : v})}>
                      <SelectTrigger className="rounded-sm h-9" data-testid="event-column-select"><SelectValue placeholder="Choose column..."/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Select —</SelectItem>
                        {columns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-500 mt-1">Each unique value in this column becomes a sub-tab.</p>
                  </div>
                )
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} className="rounded-sm bg-[#0A1128] hover:bg-slate-800" data-testid="save-record-type-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Records View ----------
function RecordsView({ sport, canManage, canReorder, setConfirmDelete }) {
  const [reorderMode, setReorderMode] = useState(false);
  const [eventFilter, setEventFilter] = useState("");
  const [events, setEvents] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cats, setCats] = useState([]);
  const [rtypes, setRtypes] = useState([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [rtFilter, setRtFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const bump = () => setReloadToken(t => t + 1);

  // Debounce search input to avoid a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Load categories + record types (small collections, once per sport).
  useEffect(() => {
    let cancelled = false;
    setCats([]); setRtypes([]); setRtFilter("all"); setCatFilter("all");
    Promise.all([
      api.get(`/categories?sport_id=${sport.id}`),
      api.get(`/record-types?sport_id=${sport.id}`),
    ]).then(([c, rt]) => { if (!cancelled) { setCats(c.data); setRtypes(rt.data); } });
    return () => { cancelled = true; };
  }, [sport.id]);

  // Reset to page 0 whenever a filter changes.
  useEffect(() => { setPage(0); }, [debouncedQ, rtFilter, catFilter, statusFilter, sport.id]);

  // Server-side pagination + search + filters.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { sport_id: sport.id, page, page_size: pageSize, manage: true };
    if (rtFilter !== "all") params.record_type = rtFilter === "__none__" ? "" : rtFilter;
    if (catFilter !== "all") params.category = catFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    if (debouncedQ) params.search = debouncedQ;
    api.get("/records", { params }).then(res => {
      if (cancelled) return;
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    }).catch(() => { if (!cancelled) { setItems([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sport.id, page, debouncedQ, rtFilter, catFilter, statusFilter, reloadToken]);

  // "load" is preserved as a reload trigger for actions (delete/edit) — it re-fetches
  // categories/types too so the filter dropdowns stay fresh when new imports run.
  const load = async () => {
    const [c, rt] = await Promise.all([
      api.get(`/categories?sport_id=${sport.id}`),
      api.get(`/record-types?sport_id=${sport.id}`),
    ]);
    setCats(c.data); setRtypes(rt.data);
    bump();
  };

  // Dynamic columns for the CURRENT page: union of `columns` from record types
  // present in the visible rows, preserving each type's original order.
  const dynamicColumns = useMemo(() => {
    const rtNames = new Set(items.map(r => r.record_type).filter(Boolean));
    const seen = new Set();
    const cols = [];
    rtypes.filter(t => rtNames.has(t.name)).forEach(t => {
      (t.columns || []).forEach(c => { if (c && !seen.has(c)) { seen.add(c); cols.push(c); } });
    });
    items.forEach(r => {
      Object.keys(r.dynamic_columns || {}).forEach(c => { if (c && !seen.has(c)) { seen.add(c); cols.push(c); } });
    });
    return cols;
  }, [items, rtypes]);

  const paged = items; // items are already the current page from the server

  const activeRt = rtypes.find(t => t.name === rtFilter);
  const grouped = !!(activeRt?.group_by_event_tabs && activeRt?.event_column);
  useEffect(() => { setReorderMode(false); setEventFilter(""); setEvents([]); }, [sport.id, rtFilter]);
  useEffect(() => {
    if (!grouped) return;
    api.get("/records/facets", { params: { sport_id: sport.id, record_type: activeRt.name, event_column: activeRt.event_column } })
      .then(r => setEvents((r.data.events || []).map(String))).catch(() => setEvents([]));
  }, [grouped, sport.id, activeRt?.name]);

  const startReorder = () => {
    if (!activeRt) {
      if (rtypes.length === 1) { setRtFilter(rtypes[0].name); toast.info(`Selected "${rtypes[0].name}" — click Reorder Mode again`); return; }
      return toast.info("Choose a record type in the filter first, then click Reorder Mode");
    }
    if (grouped && !eventFilter) return toast.info("Choose an event to reorder");
    setReorderMode(true);
  };

  const exportCsv = () => {
    const url = `${BACKEND_URL}/api/records/export?sport_id=${sport.id}`;
    window.open(url, "_blank");
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
          <Input placeholder="Search records..." value={q} onChange={e => { setQ(e.target.value); setPage(0); }} className="pl-8 rounded-sm h-9" data-testid="records-search-input"/>
        </div>
        <Select value={rtFilter} onValueChange={v => { setRtFilter(v); setPage(0); }}>
          <SelectTrigger className="w-52 rounded-sm h-9" data-testid="records-rt-filter"><SelectValue placeholder="All record types"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All record types</SelectItem>
            <SelectItem value="__none__">(No type / General)</SelectItem>
            {rtypes.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={v => { setCatFilter(v); setPage(0); }}>
          <SelectTrigger className="w-52 rounded-sm h-9" data-testid="records-cat-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {cats.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-36 rounded-sm h-9" data-testid="records-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="rounded-sm h-9" onClick={exportCsv} data-testid="export-csv-btn">
          <Download className="w-3.5 h-3.5 mr-1"/>Export
        </Button>
        {canReorder && grouped && !reorderMode && (
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-44 rounded-sm h-9" data-testid="reorder-event-select"><SelectValue placeholder="Pick event to reorder"/></SelectTrigger>
            <SelectContent>
              {events.map(ev => <SelectItem key={ev} value={ev}>{ev}</SelectItem>)}
              {events.length === 0 && <SelectItem value="__none__" disabled>No events found</SelectItem>}
            </SelectContent>
          </Select>
        )}
        {canReorder && !reorderMode && (
          <Button size="sm" className="rounded-sm h-9 bg-[#E11D48] hover:bg-[#be123c] text-white font-heading tracking-wide" onClick={startReorder} data-testid="reorder-mode-btn">
            <ArrowUpDown className="w-3.5 h-3.5 mr-1"/>Reorder Mode
          </Button>
        )}
        {canManage && (
          <Button size="sm" className="rounded-sm bg-[#0A1128] hover:bg-slate-800 h-9" onClick={() => setShowNew(true)} data-testid="add-record-btn">
            <Plus className="w-3.5 h-3.5 mr-1"/>Add Record
          </Button>
        )}
      </div>

      {reorderMode ? (
        <div className="border border-slate-200">
          <ReorderPanel sportId={sport.id} recordType={activeRt.name} eventColumn={grouped ? activeRt.event_column : null}
            eventValue={grouped ? eventFilter : null} columns={activeRt.columns || []}
            onExit={() => setReorderMode(false)} onSaved={bump}/>
        </div>
      ) : total === 0 ? (
        <div className="border-2 border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="no-records-msg">
          {loading ? "Loading..." : (
            (debouncedQ || rtFilter !== "all" || catFilter !== "all" || statusFilter !== "all")
              ? "No records match your filters."
              : canManage ? "No records yet. Import a CSV to define columns for a record type."
                : "No records assigned to your association/school in this sport. Sidebar counts include all schools' records."
          )}
        </div>
      ) : (
        <>
          <div className="border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-100">
                <TableRow>
                  <TableHead className="font-heading whitespace-nowrap">Record Type</TableHead>
                  <TableHead className="font-heading whitespace-nowrap">Category</TableHead>
                  {dynamicColumns.map(col => (
                    <TableHead key={col} className="font-heading whitespace-nowrap">{col}</TableHead>
                  ))}
                  <TableHead className="font-heading whitespace-nowrap">Status</TableHead>
                  {canManage && <TableHead className="text-right font-heading">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map(r => (
                  <TableRow key={r.id} data-testid={`admin-record-row-${r.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{r.record_type || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{r.category}</TableCell>
                    {dynamicColumns.map(col => {
                      const v = r.dynamic_columns?.[col];
                      return (
                        <TableCell key={col} className="text-sm whitespace-nowrap">
                          {v == null || v === "" ? <span className="text-slate-300">—</span> : v}
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      {r.status === "approved"
                        ? <Badge className="rounded-sm bg-green-50 text-green-700 border border-green-300">APPROVED</Badge>
                        : <Badge className="rounded-sm bg-red-50 text-red-700 border border-red-300">PENDING</Badge>}
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0" onClick={() => setEditing(r)} data-testid={`edit-record-${r.id}`}><Pencil className="w-3 h-3"/></Button>
                          <Button size="sm" variant="outline" className="rounded-sm h-8 w-8 p-0 text-red-600"
                            onClick={() => setConfirmDelete({ type: "record", id: r.id, name: Object.values(r.dynamic_columns || {})[0] || r.record_type || "record", action: async () => {
                              await api.delete(`/records/${r.id}`); toast.success("Record deleted"); load();
                            }})}
                            data-testid={`del-record-${r.id}`}><Trash2 className="w-3 h-3"/></Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            testidPrefix="records-pagination"
          />
        </>
      )}
      {(showNew || editing) && <RecordDialog sport={sport} record={editing} cats={cats} rtypes={rtypes}
        onClose={() => { setShowNew(false); setEditing(null); }} onSaved={load}/>}
    </div>
  );
}

function RecordDialog({ sport, record, cats, rtypes, onClose, onSaved }) {
  const [recordType, setRecordType] = useState(record?.record_type || "");
  const [category, setCategory] = useState(record?.category || (cats[0]?.name || ""));
  const [status, setStatus] = useState(record?.status || "approved");
  const [dyn, setDyn] = useState({ ...(record?.dynamic_columns || {}) });
  const [errors, setErrors] = useState({});

  // Columns come from the selected record type — no defaults.
  const columns = useMemo(() => {
    const rt = rtypes.find(t => t.name === recordType);
    const base = (rt?.columns || []).filter(Boolean);
    // Include any dynamic keys already on this record that aren't in the RT schema.
    const extras = Object.keys(record?.dynamic_columns || {}).filter(k => !base.includes(k));
    return [...base, ...extras];
  }, [rtypes, recordType, record]);

  const validate = () => {
    const e = {};
    if (!recordType) e.record_type = "Pick a record type";
    if (!category) e.category = "Required";
    if (columns.length === 0) e.__form = "This record type has no columns yet — import a CSV first.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) { toast.error("Fix validation errors"); return; }
    try {
      const payload = {
        record_type: recordType,
        category,
        status,
        dynamic_columns: Object.fromEntries(columns.map(c => [c, dyn[c] ?? ""])),
      };
      if (record) await api.patch(`/records/${record.id}`, payload);
      else await api.post("/records", { sport_id: sport.id, ...payload });
      toast.success(record ? "Record updated" : "Record added");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="rounded-none border-2 border-[#0A1128] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="record-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{record ? "EDIT RECORD" : "NEW RECORD"}</DialogTitle>
          <DialogDescription>Fields below are defined by the CSV imported for the selected record type.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Record Type *</Label>
              <Select value={recordType || "__none__"} onValueChange={v => setRecordType(v === "__none__" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="record-record-type"><SelectValue placeholder="Select..."/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Select —</SelectItem>
                  {(rtypes || []).map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.record_type && <p className="text-xs text-red-600 mt-1">{errors.record_type}</p>}
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="rounded-sm" data-testid="record-category"><SelectValue placeholder="Select..."/></SelectTrigger>
                <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              {errors.category && <p className="text-xs text-red-600 mt-1">{errors.category}</p>}
            </div>
          </div>

          {recordType && (
            columns.length === 0 ? (
              <div className="text-sm text-slate-600 border-l-4 border-[#FACC15] pl-3 py-2 bg-yellow-50/40">
                No columns defined for <strong>{recordType}</strong> yet. Import a CSV for this record type first — its column headers will become the fields shown here.
              </div>
            ) : (
              <>
                <p className="font-overline text-[10px] text-slate-500 pt-2 border-t">Fields for {recordType}</p>
                <div className="grid grid-cols-2 gap-3">
                  {columns.map(col => (
                    <div key={col}>
                      <Label className="font-mono text-xs">{col}</Label>
                      <Input value={dyn[col] ?? ""} onChange={e => setDyn({ ...dyn, [col]: e.target.value })} className="rounded-sm" data-testid={`record-dyn-${col}`}/>
                    </div>
                  ))}
                </div>
              </>
            )
          )}

          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="rounded-sm" data-testid="record-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {errors.__form && <p className="text-xs text-red-600">{errors.__form}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button
            onClick={save}
            disabled={!recordType || columns.length === 0}
            className="rounded-sm bg-[#0A1128] hover:bg-slate-800"
            data-testid="save-record-btn"
          >Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ field, value, onChange }) {
  const common = { value: value || "", onChange: e => onChange(e.target.value), className: "rounded-sm", placeholder: field.placeholder || "", "data-testid": `cv-${field.key}` };
  if (field.type === "textarea") return <Textarea {...common}/>;
  if (field.type === "number") return <Input type="number" step="1" {...common}/>;
  if (field.type === "decimal") return <Input type="number" step="0.01" {...common}/>;
  if (field.type === "date") return <Input type="date" {...common}/>;
  if (field.type === "boolean") return (
    <Select value={String(value)} onValueChange={v => onChange(v === "true")}>
      <SelectTrigger className="rounded-sm" data-testid={`cv-${field.key}`}><SelectValue placeholder="—"/></SelectTrigger>
      <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
    </Select>
  );
  if (field.type === "dropdown") return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="rounded-sm" data-testid={`cv-${field.key}`}><SelectValue placeholder="—"/></SelectTrigger>
      <SelectContent>{(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  return <Input {...common}/>;
}
